import { useGetTemplateBySlug } from '@/modules/templates/hooks/use-templates';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { TypeformBuilder } from '../builder/typeform-builder';

export default function FormBuilderPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const templateId = params.id as string;

  // We use the slug-based hook. If routing is by ID we may need to adapt,
  // but the hook returns TemplateWithScreens which has screens[].fields[].
  // If the route param is actually the slug, this works directly.
  const {
    data: templateData,
    isLoading: loading,
    error: queryError,
  } = useGetTemplateBySlug(templateId);

  const template = templateData ?? null;
  const screens = template?.screens ?? [];
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : t('ORGTEMPLATES_BUILDER_ERRORS_LOADFAILED')
    : null;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[hsl(var(--bg))]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-selise-blue" />
          <p className="text-sm text-globe-grey">{t('ORGTEMPLATES_BUILDER_LOADING')}</p>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="h-screen flex items-center justify-center bg-[hsl(var(--bg))]">
        <div className="text-center max-w-md p-8">
          <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
            <svg
              className="h-8 w-8 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-[hsl(var(--fg))] mb-2">
            {error || t('ORGTEMPLATES_BUILDER_ERRORS_NOTFOUND')}
          </h2>
          <p className="text-sm text-globe-grey mb-6">
            {t('ORGTEMPLATES_BUILDER_ERRORS_UNABLETOLOAD')}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-selise-blue text-white font-medium hover:bg-oxford-blue transition-colors"
          >
            {t('ORGTEMPLATES_BUILDER_GOBACK')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <TypeformBuilder
      template={{
        id: template.id,
        slug: template.slug,
        title: template.title,
        description: template.description ?? '',
        available: template.available ?? false,
        previewToken: template.previewToken ?? null,
        organizationId: template.organizationId ?? null,
      }}
      initialScreens={screens}
    />
  );
}
